import axios from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Loading from '../components/Loading/Loading'
import { useAuth } from './AuthContext'
import { refreshAccessToken } from './axiosInstance'

export const baseAuthUrl = import.meta.env.VITE_API_URL
console.log(
  `[NODE_ENV]: ${import.meta.env.MODE}\n[baseAuthUrl]: ${baseAuthUrl}\n[REACT_APP_ENV]: ${import.meta.env.VITE_ENV}`
)
// console.log('baseAuthUrl: ', baseAuthUrl)
// console.log('NODE_ENV: ', import.meta.env.MODE)
// console.log('REACT_APP_ENV: ', import.meta.env.VITE_ENV)

const AuthCallback = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [googleAdsStatus, setGoogleAdsStatus] = useState<'loading' | 'success' | 'error' | null>(null)
  const [googleAdsMessage, setGoogleAdsMessage] = useState('')
  const { redirectToSlackAuth, redirectToPandaAuth } = useAuth()

  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const error = urlParams.get('error')

      console.log('Code:', code)
      console.log('State:', state)

      // Handle Google Ads OAuth callback (opens in popup)
      if (state === 'google_ads') {
        setGoogleAdsStatus('loading')

        if (error) {
          setGoogleAdsStatus('error')
          setGoogleAdsMessage(`Authentication failed: ${error}`)
          return
        }

        if (!code) {
          setGoogleAdsStatus('error')
          setGoogleAdsMessage('No authorization code received from Google')
          return
        }

        try {
          const response = await axios.post(`${baseAuthUrl}/api/activity-tracker/google-ads/auth/exchange`, { code })

          if (response.data.success) {
            setGoogleAdsStatus('success')
            setGoogleAdsMessage(response.data.message || 'Google Ads API token updated successfully!')

            // Notify opener window
            if (window.opener) {
              window.opener.postMessage({ type: 'GOOGLE_ADS_AUTH_SUCCESS' }, '*')
            }

            // Auto-close popup after 3 seconds
            setTimeout(() => window.close(), 3000)
          } else {
            setGoogleAdsStatus('error')
            setGoogleAdsMessage(response.data.message || 'Failed to update token')
          }
        } catch (err: any) {
          console.error('Google Ads auth exchange error:', err)
          setGoogleAdsStatus('error')
          setGoogleAdsMessage(err.response?.data?.message || err.message || 'Token exchange failed')
        } finally {
          setIsLoading(false)
        }
        return
      }

      if (!code || !state) {
        console.error('No authorization code or state found in URL')
        navigate('/')
        return
      }

      try {
        if (state === 'google') {
          console.log('Google state detected')
          console.log('Google Code: ', code)
          localStorage.setItem('google_code', code)
          redirectToSlackAuth()
        } else if (state === 'slack') {
          console.log('Slack state detected')
          localStorage.setItem('slack_code', code)

          if (import.meta.env.VITE_ENV === 'local' || import.meta.env.VITE_ENV === 'laptop') {
            console.log('Local environment detected, skipping PandaDoc auth')

            const googleCode = localStorage.getItem('google_code')
            const slackCode = localStorage.getItem('slack_code')

            if (!googleCode || !slackCode) {
              console.error('Authorization code missing for one or more services')
              navigate('/')
              return
            }

            const response = await axios.post(`${baseAuthUrl}/aws/combined-authorization`, {
              googleCode,
              slackCode,
              pandaCode: 'dummy_panda_code', // Use a dummy code as it's not needed in local
            })

            console.log('Response in AuthCallback:', response)

            const {
              googleAccessToken,
              googleRefreshToken: _googleRefreshToken,
              googleExpiresIn,
              slackAccessToken,
              slackUserAccessToken,
              slackUserId,
            } = response.data

            console.log('Google Access Token:', googleAccessToken)
            console.log('Slack Access Token:', slackAccessToken)

            // Set the static PandaDoc token for local environment
            const pandaAccessToken = '44c682867cd90875b3ba53db7280c56e1a4d6068'
            const _pandaRefreshToken = ''

            localStorage.setItem('google_access_token', googleAccessToken)
            setTimeout(refreshAccessToken, (googleExpiresIn - 60) * 1000)
            // localStorage.setItem('google_refresh_token', googleRefreshToken)
            localStorage.setItem('slack_access_token', slackAccessToken)
            localStorage.setItem('slack_user_access_token', slackUserAccessToken)
            localStorage.setItem('slack_user_id', slackUserId)
            localStorage.setItem('panda_access_token', pandaAccessToken)
            localStorage.setItem('panda_refresh_token', _pandaRefreshToken)

            navigate('/dashboard')
          } else {
            redirectToPandaAuth()
          }
        } else if (state === 'panda') {
          console.log('Panda state detected')
          localStorage.setItem('panda_code', code)

          const googleCode = localStorage.getItem('google_code')
          const slackCode = localStorage.getItem('slack_code')
          const pandaCode = localStorage.getItem('panda_code')

          console.log('Google Code:', googleCode)
          console.log('Slack Code:', slackCode)
          console.log('Panda Code:', pandaCode)

          if (!googleCode || !slackCode || !pandaCode) {
            console.error('Authorization code missing for one or more services')
            navigate('/')
            return
          }

          const response = await axios.post(`${baseAuthUrl}/aws/combined-authorization`, {
            googleCode,
            slackCode,
            pandaCode,
          })

          console.log('Response in AuthCallback:', response)

          const {
            googleAccessToken,
            googleRefreshToken: _googleRefreshToken2,
            googleExpiresIn,
            slackAccessToken,
            slackUserAccessToken,
            slackUserId,
            pandaAccessToken,
            pandaRefreshToken,
          } = response.data

          console.log('Google Access Token:', googleAccessToken)
          console.log('Slack Access Token:', slackAccessToken)
          console.log('Panda Access Token:', pandaAccessToken)

          localStorage.setItem('google_access_token', googleAccessToken)
          setTimeout(refreshAccessToken, (googleExpiresIn - 60) * 1000)

          // localStorage.setItem('google_refresh_token', googleRefreshToken)
          localStorage.setItem('slack_access_token', slackAccessToken)
          localStorage.setItem('slack_user_access_token', slackUserAccessToken)
          localStorage.setItem('slack_user_id', slackUserId)
          localStorage.setItem('panda_access_token', pandaAccessToken)
          localStorage.setItem('panda_refresh_token', pandaRefreshToken)

          navigate('/dashboard')
        }
      } catch (error) {
        console.error('Error exchanging tokens:', error)
        navigate('/')
      } finally {
        setIsLoading(false)
      }
    }

    handleAuthCallback()
  }, [navigate, redirectToSlackAuth])

  // Google Ads OAuth callback UI (shown in popup)
  if (googleAdsStatus) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'system-ui, sans-serif',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
      }}>
        {googleAdsStatus === 'loading' && (
          <>
            <div style={{
              width: 40,
              height: 40,
              border: '3px solid #333',
              borderTopColor: '#4caf50',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ marginTop: 20, color: '#888' }}>Exchanging authorization code...</p>
          </>
        )}

        {googleAdsStatus === 'success' && (
          <>
            <div style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
            }}>
              <span style={{ fontSize: 32, color: 'white' }}>✓</span>
            </div>
            <h2 style={{ margin: '0 0 10px', color: '#4caf50' }}>Authentication Successful!</h2>
            <p style={{ color: '#888', margin: 0 }}>{googleAdsMessage}</p>
            <p style={{ color: '#666', marginTop: 20, fontSize: 14 }}>This window will close automatically...</p>
          </>
        )}

        {googleAdsStatus === 'error' && (
          <>
            <div style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: '#d32f2f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
            }}>
              <span style={{ fontSize: 32, color: 'white' }}>✕</span>
            </div>
            <h2 style={{ margin: '0 0 10px', color: '#d32f2f' }}>Authentication Failed</h2>
            <p style={{ color: '#888', margin: 0 }}>{googleAdsMessage}</p>
            <button
              onClick={() => window.close()}
              style={{
                marginTop: 20,
                padding: '10px 20px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Close Window
            </button>
          </>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Loading />
      </div>
    )
  }

  return null
}

export default AuthCallback
